import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaTrophy, FaCoins, FaFire, FaGraduationCap, FaChartLine, FaDollarSign, FaWallet, FaMedal } from 'react-icons/fa';
import UserService from '../services/UserService';
import Badge from './Badge';
import { getLevelTitle } from '../utils/levelUtils';
import toast from 'react-hot-toast';
import './UserStatsModal.css';

const UserStatsModal = ({ isOpen, onClose, userId, userName, userProfilePictureUrl }) => {
  const [loading, setLoading] = useState(true);
  const [userStats, setUserStats] = useState(null);
  const [userInfo, setUserInfo] = useState(null);

  const loadUserStats = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    setUserStats(null);
    setUserInfo(null);
    
    try {
      const data = await UserService.getUserStatsById(userId);
      setUserStats(data.stats);
      setUserInfo(data.user);
    } catch (error) {
      console.error('Error loading user stats:', error);
      toast.error('Failed to load user stats');
      setUserStats(null);
      setUserInfo(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (isOpen && userId) {
      loadUserStats();
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else if (!isOpen) {
      // Reset state when modal closes
      setLoading(true);
      setUserStats(null);
      setUserInfo(null);
      // Restore body scroll when modal closes
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scroll on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, userId, loadUserStats]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };

  const getDefaultAvatarColor = (name) => {
    if (!name) return '#2563eb';
    const colors = [
      '#2563eb', '#10b981', '#f59e0b', '#ef4444', 
      '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="user-stats-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="user-stats-modal"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="user-stats-modal-header">
              <button className="user-stats-modal-close" onClick={onClose}>
                <FaTimes />
              </button>
            </div>

            {loading ? (
              <div className="user-stats-modal-loading">
                <div className="loading-spinner"></div>
                <p>Loading stats...</p>
              </div>
            ) : userStats && userInfo ? (
              <div className="user-stats-modal-content">
                {/* User Profile Section */}
                <div className="user-stats-profile">
                  {userInfo.profilePictureUrl ? (
                    <img 
                      src={userInfo.profilePictureUrl} 
                      alt={userInfo.name}
                      className="user-stats-avatar"
                    />
                  ) : (
                    <div 
                      className="user-stats-avatar-default"
                      style={{ backgroundColor: getDefaultAvatarColor(userInfo.name) }}
                    >
                      <span className="user-stats-initials">{getInitials(userInfo.name)}</span>
                    </div>
                  )}
                  <h2 className="user-stats-name">{userInfo.name}</h2>
                  <div className="user-stats-level">
                    Level {userStats.level || 1} • {getLevelTitle(userStats.level || 1)}
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="user-stats-grid">
                  <div className="user-stat-card">
                    <div className="user-stat-icon xp">
                      <FaCoins />
                    </div>
                    <div className="user-stat-value">{(userStats.xp || 0).toLocaleString()}</div>
                    <div className="user-stat-label">Total XP</div>
                  </div>

                  <div className="user-stat-card">
                    <div className="user-stat-icon streak">
                      <FaFire />
                    </div>
                    <div className="user-stat-value">{userStats.streak || 0}</div>
                    <div className="user-stat-label">Day Streak</div>
                  </div>

                  <div className="user-stat-card">
                    <div className="user-stat-icon portfolio">
                      <FaWallet />
                    </div>
                    <div className="user-stat-value">{formatCurrency(userStats.portfolioValue || 0)}</div>
                    <div className="user-stat-label">Portfolio Value</div>
                  </div>

                  <div className="user-stat-card">
                    <div className="user-stat-icon earnings">
                      <FaDollarSign />
                    </div>
                    <div className="user-stat-value">{formatCurrency(userStats.totalEarnings || 0)}</div>
                    <div className="user-stat-label">Total Earnings</div>
                  </div>

                  <div className="user-stat-card">
                    <div className="user-stat-icon investing">
                      <FaChartLine />
                    </div>
                    <div className="user-stat-value">{formatCurrency(userStats.investingEarnings || 0)}</div>
                    <div className="user-stat-label">Investing Earnings</div>
                  </div>

                  <div className="user-stat-card">
                    <div className="user-stat-icon quizzes">
                      <FaGraduationCap />
                    </div>
                    <div className="user-stat-value">{formatCurrency(userStats.quizEarnings || 0)}</div>
                    <div className="user-stat-label">Quiz Earnings</div>
                  </div>

                  <div className="user-stat-card">
                    <div className="user-stat-icon facts">
                      <FaMedal />
                    </div>
                    <div className="user-stat-value">{userStats.completedFacts || 0}</div>
                    <div className="user-stat-label">Facts Completed</div>
                  </div>

                  <div className="user-stat-card">
                    <div className="user-stat-icon lessons">
                      <FaGraduationCap />
                    </div>
                    <div className="user-stat-value">{userStats.completedLessons || 0}</div>
                    <div className="user-stat-label">Lessons Completed</div>
                  </div>
                </div>

                {/* Badges Section */}
                {userStats.badges && userStats.badges.length > 0 && (
                  <div className="user-stats-badges">
                    <h3 className="user-stats-section-title">
                      <FaTrophy /> Badges Earned
                    </h3>
                    <div className="user-stats-badges-list">
                      {userStats.badges.map((badge, index) => (
                        <Badge
                          key={badge.id || index}
                          badge={badge}
                          size="large"
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="user-stats-modal-error">
                <p>Failed to load user stats</p>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default UserStatsModal;

